import React, { useMemo } from 'react';
import { FiAlertTriangle, FiCalendar, FiClock, FiUser } from 'react-icons/fi';
import type { AgentLicense, HREmployee, HRKpis, Happening } from './hrData';
import { getExpiringLicenses, usDate } from './licenseUtils';

interface Props {
  me: HREmployee;
  /** Every employee for admins; just `me` otherwise. */
  employees: HREmployee[];
  kpis: HRKpis;
  /** Already limited by the API to a few days back through the next month. */
  happenings: Happening[];
  admin: boolean;
  /** Admin only — the expiring-licenses panel only renders when this is passed. */
  licenses?: {
    alertDays: number;
    onCreateCommand: (employee: HREmployee, license: AgentLicense) => void;
  };
}

const HAPPENING_ICON: Record<Happening['kind'], React.ReactNode> = {
  birthday: <FiCalendar size={13} />,
  anniversary: <FiClock size={13} />,
  pto: <FiCalendar size={13} />,
  outofoffice: <FiUser size={13} />,
};

const formatHours = (h: number) => `${h.toFixed(1)} hrs`;

const HRDashboard: React.FC<Props> = ({ me, employees, kpis, happenings, admin, licenses }) => {
  const expiringLicenses = useMemo(
    () => (admin && licenses ? getExpiringLicenses(employees, licenses.alertDays, new Date()) : []),
    [admin, employees, licenses]
  );

  const upcoming = useMemo(() => happenings.slice(0, 6), [happenings]);

  return (
    <div className="hr-dash">
      <div className="hr-dash__kpis">
        <div className="hr-dash__kpi">
          <div className="hr-dash__kpi-label">{admin ? 'Approved Hours Off (YTD)' : 'Your Hours Off (YTD)'}</div>
          <div className="hr-dash__kpi-val hr-dash__kpi-val--green">{kpis.approvedHours.toFixed(1)}</div>
        </div>
        <div className="hr-dash__kpi">
          <div className="hr-dash__kpi-label">{admin ? 'Pending Requests' : 'Your Pending Requests'}</div>
          <div className={`hr-dash__kpi-val${kpis.pendingCount ? ' hr-dash__kpi-val--warn' : ''}`}>{kpis.pendingCount}</div>
        </div>
        <div className="hr-dash__kpi">
          <div className="hr-dash__kpi-label">Your PTO Balance</div>
          <div className={`hr-dash__kpi-val ${me.ptoBalanceHours < 0 ? 'hr-dash__kpi-val--negative' : 'hr-dash__kpi-val--blue'}`}>
            {me.ptoBalanceHours.toFixed(1)}
          </div>
        </div>
        <div className="hr-dash__kpi">
          <div className="hr-dash__kpi-label">People</div>
          <div className="hr-dash__kpi-val">{kpis.peopleCount}</div>
        </div>
      </div>

      {licenses && expiringLicenses.length > 0 && (
        <section className="hr-dash__licenses">
          <h3 className="hr-dash__col-title">Expiring licenses</h3>
          {expiringLicenses.map(({ employee, license, days, urgency }) => (
            <div key={license.id} className="hr-dash__license-row">
              <FiAlertTriangle size={13} className={`hr-dash__license-icon hr-dash__license-icon--${urgency}`} aria-hidden="true" />
              <span className="hr-dash__license-name">{employee.name}</span>
              <span className="hr-dash__license-meta">{license.state} · {license.linesOfAuthority}</span>
              <span className={`hr-dash__license-days hr-dash__license-days--${urgency}`}>
                {days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? 'Expires today' : `${days}d left`}
              </span>
              {license.commandTaskId ? (
                <span className="hr-lic-editor__cmd-done">Command created</span>
              ) : (
                <button type="button" className="hr-lic-editor__cmd-btn" onClick={() => licenses.onCreateCommand(employee, license)}>
                  Create Command
                </button>
              )}
            </div>
          ))}
        </section>
      )}

      <div className="hr-dash__cols">
        <section className="hr-dash__col">
          <h3 className="hr-dash__col-title">What's happening</h3>
          {upcoming.length === 0 ? (
            <p className="hr-dash__empty">Nothing on the calendar this month.</p>
          ) : (
            <ul className="hr-dash__happenings">
              {upcoming.map((h, i) => (
                <li key={`${h.kind}-${h.employeeId}-${h.date}-${i}`} className={`hr-dash__happening hr-dash__happening--${h.kind}`}>
                  <span className="hr-dash__happening-icon">{HAPPENING_ICON[h.kind]}</span>
                  <span>
                    <span className="hr-dash__happening-date">{usDate(h.date)}</span>
                    {h.label}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="hr-dash__col">
          <h3 className="hr-dash__col-title">{admin ? 'PTO balances' : 'Your PTO balance'}</h3>
          <ul className="hr-dash__balances">
            {employees.map(e => (
              <li key={e.id} className="hr-dash__balance">
                <span className="hr-dash__balance-name">{e.name}</span>
                <span className={`hr-dash__balance-hrs${e.ptoBalanceHours < 0 ? ' hr-dash__balance-hrs--negative' : ''}`}>
                  {formatHours(e.ptoBalanceHours)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default HRDashboard;
